import React, { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import TextInput from './input-components/text-input';
import type { InputProps } from 'antd/lib/input';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import Tooltip from 'components/common/tooltip';
import debounce from 'lodash/debounce';
import { isTestNodeEnv } from 'constants/config';
import type { InputRef } from 'antd';
import { useIntl } from 'react-intl';
import Markdown from 'components/common/markdown';

export interface SearchBarProps extends InputProps {
  placeholder?: string;
  /** `placeholder` is a translation key, so a parameterised one needs its values. TextInput formats both. */
  placeholderValues?: Record<string, string>;
  onFilter: (query: string) => void;
  /**
   * The search text, for a caller that controls it. Every keystroke then reaches `onFilter` at once, since the
   * field shows only what comes back. Not `value`: AutoComplete injects its own `value` into this field.
   */
  query?: string;
  debounceMs?: number;
  hideError?: boolean;
  error?: string | null;
  showExtendedSearchSyntax?: boolean;
}

export const DEFAULT_DEBOUNCE = isTestNodeEnv ? 5 : 250;

// eslint-disable-next-line react/display-name
const SearchBar = React.forwardRef<InputRef, SearchBarProps>(
  (
    {
      onFilter,
      placeholder,
      placeholderValues,
      hideError,
      debounceMs = DEFAULT_DEBOUNCE,
      value,
      query,
      error,
      allowClear,
      showExtendedSearchSyntax = false,
      ...otherProps
    }: SearchBarProps,
    ref
  ) => {
    const isControlled = query !== undefined;
    const onFilterWithDebounce = useMemo(() => {
      if (isControlled) return onFilter;

      return debounce(async searchValue => {
        onFilter(searchValue);
      }, debounceMs || 250);
    }, [isControlled, debounceMs, onFilter]);

    const setSearch = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onFilterWithDebounce(e.target.value);
      },
      [onFilterWithDebounce]
    );

    const intl = useIntl();

    const tooltipContent = useMemo(() => {
      let content: string = placeholder ? intl.formatMessage({ id: placeholder }, placeholderValues) : '';
      if (showExtendedSearchSyntax) {
        if (content) {
          content += '\n\n---\n\n';
        }
        content += intl.formatMessage({ id: 'search-bar.extended-search-syntax.tooltip' });
      }
      return content;
    }, [placeholder, placeholderValues, showExtendedSearchSyntax, intl]);

    // `right` opened clear of a suffix icon at the field's right edge. From the prefix it would open
    // across the field's own text, so the 320px overlay drops below it instead.
    const searchIcon = (
      <Tooltip title={<StyledMarkdown markdown={tooltipContent} />} placement="bottomLeft">
        <SearchOutlined />
      </Tooltip>
    );

    const resolvedAllowClear = allowClear === false ? false : { clearIcon: <CloseOutlined /> };

    return (
      <StyledTextInput
        data-e2e="search-bar"
        error={error}
        placeholder={placeholder}
        placeholderValues={placeholderValues}
        hideError={!!hideError}
        allowClear={resolvedAllowClear}
        {...{ prefix: searchIcon }}
        {...otherProps}
        value={query}
        ref={ref}
        onChange={setSearch}
      />
    );
  }
);

const StyledTextInput = styled(TextInput)`
  .ant-input-prefix .anticon {
    font-size: var(--scale-500);
    color: var(--component-field-icon);
  }

  &.ant-input-affix-wrapper-disabled .ant-input-prefix .anticon {
    color: var(--component-field-icon-disabled);
  }
`;

const StyledMarkdown = styled(Markdown)`
  &&& p:last-of-type {
    margin-bottom: 0;
  }
`;

export default SearchBar;
